import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";

import { isOffline } from "../../actions/Devices/util";
import { callAvosApi } from "../../utils/useAvosApiHook";

dayjs.extend(relativeTime);

export interface Device {
    UID?: string;
    device_id?: string;
    last_seen?: string;
    name?: string;
}

// Custom hook to get last seen information
export function getDeviceLastSeen(
    device: Device | undefined,
    refresh: boolean
): [Device | undefined, boolean, string | false, number] {
    const [now, setNow] = useState < number >(Date.now());
    const [deviceState, setDevice] = useState < Device | undefined >(device);

    useEffect(() => {
        setDevice(device);
        let interval: ReturnType<typeof setInterval> | undefined;
        if (device) {
            interval = setInterval(() => {
                setNow(Date.now());
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [device?.device_id, device?.last_seen]);

    useEffect(() => {
        let pollInterval: ReturnType<typeof setInterval> | undefined;
        if (refresh && device?.UID) {
            pollInterval = setInterval(() => {
                callAvosApi(`/device/management/${device.UID}/`).then((response) => {
                    setDevice(response.data);
                });
            }, 30000);
        }

        return () => {
            if (pollInterval) clearInterval(pollInterval);
        };
    }, [refresh, device?.UID]);

    if (!device || !deviceState?.last_seen) {
        return [deviceState, true, false, now];
    }

    const offline = isOffline(deviceState);
    const lastSeenRelative = dayjs.utc(deviceState.last_seen).fromNow(true);

    return [deviceState, offline, lastSeenRelative, now];
}

interface DeviceLastSeenProps {
    device?: Device;
    prefix?: string | false;
    refresh?: boolean;
}

export default function DeviceLastSeen({
    device,
    prefix = "Last activity",
    refresh = false,
}: DeviceLastSeenProps) {
    const [deviceState, offline, lastSeenRelative] = getDeviceLastSeen(device, refresh);

    if (!deviceState?.last_seen) {
        return <span>-</span>;
    }

    return (
        <span className={!offline ? "text-success" : ""}>
            {prefix ? `${prefix} ` : ""}
            {lastSeenRelative} ago
        </span>
    );
}
